<template>
    <Head title="Enterprise Application - RentRisk" />

    <div class="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
        <div class="max-w-3xl mx-auto">
            <div class="flex items-center justify-between mb-6">
                <span class="w-16"></span>
                <jet-authentication-card-logo-colored />
                <Link :href="route('logout')" method="post" as="button" class="w-16 text-right text-sm text-gray-500 hover:text-[#2A375E]">Sign out</Link>
            </div>

            <div class="bg-white rounded-lg shadow-lg p-6 sm:p-8">
                <div class="mb-6">
                    <h1 class="text-2xl font-bold text-[#2A375E]">Enterprise Application</h1>
                    <p class="text-gray-600 text-sm mt-1">
                        Step {{ currentStep + 1 }} of {{ steps.length }} — {{ steps[currentStep] }}
                    </p>
                    <div class="mt-3 h-1.5 w-full bg-gray-200 rounded">
                        <div class="h-1.5 bg-[#2A375E] rounded transition-all" :style="{ width: progress + '%' }"></div>
                    </div>
                    <div v-if="resumed" class="mt-3 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
                        Welcome back — your details<span v-if="signedCount"> and signatures</span> were saved.
                        Your uploaded files can't be carried over, so please re-attach them on the Documents step to finish.
                    </div>
                </div>

                <!-- Step 0: Get started — corporate email first so a rejected
                     domain (Gmail etc.) is caught immediately, not at submit. -->
                <section v-if="currentStep === 0" class="space-y-4">
                    <FieldText id="company_email" label="Corporate email" v-model="form.company_email" :error="form.errors.company_email || emailDomainError" hint="Free providers (Gmail, Yahoo, Outlook, etc.) are not accepted." required />

                    <p class="text-sm text-gray-600 pt-2">What best describes your business?<span class="text-red-500"> *</span></p>
                    <div class="space-y-2">
                        <label
                            v-for="(label, value) in config.business_types"
                            :key="value"
                            class="flex items-start gap-3 border rounded-md p-3 cursor-pointer hover:bg-gray-50"
                            :class="form.business_type === value ? 'border-[#2A375E] ring-1 ring-[#2A375E]' : 'border-gray-300'"
                        >
                            <input type="radio" :value="value" v-model="form.business_type" class="mt-1 h-4 w-4 text-[#2A375E] focus:ring-[#2A375E]" />
                            <span class="text-sm text-gray-800">{{ label }}</span>
                        </label>
                    </div>
                    <p v-if="form.errors.business_type" class="text-sm text-red-600">{{ form.errors.business_type }}</p>
                </section>

                <!-- Step 1: Identity & business -->
                <section v-if="currentStep === 1" class="space-y-4">
                    <FieldText id="legal_entity_name" label="Legal entity name" v-model="form.legal_entity_name" :error="form.errors.legal_entity_name" required />

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">DBAs / other company names</label>
                        <div v-for="(dba, i) in form.dbas" :key="i" class="flex gap-2 mb-2">
                            <input type="text" v-model="form.dbas[i]" class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border" />
                            <button type="button" @click="form.dbas.splice(i, 1)" class="text-gray-400 hover:text-red-600 px-2">&times;</button>
                        </div>
                        <button type="button" @click="form.dbas.push('')" class="text-sm text-[#2A375E] hover:underline">+ Add DBA</button>
                    </div>

                    <FieldText id="physical_address" label="Physical address" v-model="form.physical_address" :error="form.errors.physical_address" required />
                    <FieldText id="phone" label="Publicly-listed phone" v-model="form.phone" :error="form.errors.phone" required />
                    <FieldText id="company_website" label="Company website" v-model="form.company_website" :error="form.errors.company_website" placeholder="https://www.example.com" hint="Include https:// or http:// (e.g. https://rentrisk.com)." />
                    <FieldText id="num_property_managers" label="Number of property managers" type="number" v-model="form.num_property_managers" :error="form.errors.num_property_managers" />
                    <FieldText id="time_in_business" label="Time in business" v-model="form.time_in_business" :error="form.errors.time_in_business" placeholder="e.g. 8 years" required />

                    <div>
                        <label for="ownership_type" class="block text-sm font-medium text-gray-700 mb-1">Type of ownership<span class="text-red-500"> *</span></label>
                        <select id="ownership_type" v-model="form.ownership_type" class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border bg-white">
                            <option value="" disabled>Select ownership type</option>
                            <option v-for="(label, value) in config.ownership_types" :key="value" :value="value">{{ label }}</option>
                        </select>
                        <p v-if="form.errors.ownership_type" class="text-sm text-red-600">{{ form.errors.ownership_type }}</p>
                    </div>

                    <FieldText id="tax_id" label="Tax ID (EIN/TIN)" v-model="form.tax_id" :error="form.errors.tax_id" required />

                    <div>
                        <label for="business_type_description" class="block text-sm font-medium text-gray-700 mb-1">Brief description of your business</label>
                        <textarea id="business_type_description" v-model="form.business_type_description" rows="3" class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border"></textarea>
                        <p v-if="form.errors.business_type_description" class="text-sm text-red-600">{{ form.errors.business_type_description }}</p>
                    </div>

                    <!-- RAC-10281: business verification -->
                    <div class="pt-2 border-t">
                        <h3 class="text-sm font-semibold text-gray-800 mb-2">Business registration &amp; licensing</h3>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <FieldText id="sos_registration_number" label="Secretary of State registration #" v-model="form.sos_registration_number" :error="form.errors.sos_registration_number" required />
                            <FieldText id="sos_registration_state" label="Registration state" v-model="form.sos_registration_state" :error="form.errors.sos_registration_state" placeholder="e.g. TX" required />
                            <FieldText id="pm_license_number" label="Property management license #" v-model="form.pm_license_number" :error="form.errors.pm_license_number" hint="Optional — leave blank if your state doesn't require one." />
                            <FieldText id="pm_license_state" label="License state" v-model="form.pm_license_state" :error="form.errors.pm_license_state" placeholder="e.g. TX" />
                        </div>
                    </div>

                    <div class="pt-2 border-t">
                        <h3 class="text-sm font-semibold text-gray-800 mb-2">Responsible owner / officer</h3>
                        <p class="text-xs text-gray-500 mb-2">The single accountable person for this account's use of consumer reports.</p>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <FieldText id="responsible_officer_name" label="Full name" v-model="form.responsible_officer.name" :error="form.errors['responsible_officer.name']" required />
                            <FieldText id="responsible_officer_title" label="Title" v-model="form.responsible_officer.title" :error="form.errors['responsible_officer.title']" required />
                            <FieldText id="responsible_officer_ownership" label="Ownership %" type="number" v-model="form.responsible_officer.ownership_percentage" :error="form.errors['responsible_officer.ownership_percentage']" placeholder="0–100" />
                            <FieldText id="responsible_officer_email" label="Contact email" v-model="form.responsible_officer.email" :error="form.errors['responsible_officer.email']" required />
                            <FieldText id="responsible_officer_phone" label="Contact phone" v-model="form.responsible_officer.phone" :error="form.errors['responsible_officer.phone']" />
                        </div>
                    </div>
                </section>

                <!-- Step 2: Principals & agents -->
                <section v-if="currentStep === 2" class="space-y-6">
                    <div>
                        <h2 class="text-sm font-semibold text-gray-800 mb-2">Principals / Owners<span class="text-red-500"> *</span></h2>
                        <div v-for="(principal, i) in form.principals" :key="i" class="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                            <input type="text" v-model="principal.name" placeholder="Full name" class="rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border" />
                            <div class="flex gap-2">
                                <input type="text" v-model="principal.title" placeholder="Title" class="rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border w-full" />
                                <button v-if="form.principals.length > 1" type="button" @click="form.principals.splice(i, 1)" class="text-gray-400 hover:text-red-600 px-2">&times;</button>
                            </div>
                        </div>
                        <button type="button" @click="form.principals.push({ name: '', title: '' })" class="text-sm text-[#2A375E] hover:underline">+ Add principal</button>
                        <p v-if="form.errors.principals" class="text-sm text-red-600">{{ form.errors.principals }}</p>
                    </div>

                    <div>
                        <h2 class="text-sm font-semibold text-gray-800 mb-2">Technical providers / agents <span class="font-normal text-gray-500">(optional)</span></h2>
                        <div v-for="(agent, i) in form.agents" :key="i" class="flex gap-2 mb-2">
                            <input type="text" v-model="form.agents[i]" placeholder="Provider name" class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border" />
                            <button type="button" @click="form.agents.splice(i, 1)" class="text-gray-400 hover:text-red-600 px-2">&times;</button>
                        </div>
                        <button type="button" @click="form.agents.push('')" class="text-sm text-[#2A375E] hover:underline">+ Add provider</button>
                    </div>
                </section>

                <!-- Step 3: Attestations -->
                <section v-if="currentStep === 3" class="space-y-6">
                    <div>
                        <p class="text-sm text-gray-800 font-medium">
                            Are any owners or beneficiaries located in, headquartered in, or organized under the laws of
                            {{ config.sanctioned_countries.join(', ') }}?<span class="text-red-500"> *</span>
                        </p>
                        <div class="flex gap-4 mt-2">
                            <label class="flex items-center gap-2 text-sm"><input type="radio" :value="false" v-model="form.has_sanctioned_owner" class="h-4 w-4 text-[#2A375E] focus:ring-[#2A375E]" /> No</label>
                            <label class="flex items-center gap-2 text-sm"><input type="radio" :value="true" v-model="form.has_sanctioned_owner" class="h-4 w-4 text-[#2A375E] focus:ring-[#2A375E]" /> Yes</label>
                        </div>
                        <p v-if="form.has_sanctioned_owner" class="text-sm text-red-600 mt-2">
                            We're unable to complete this application online. Please contact RentRisk directly.
                        </p>
                        <p v-if="form.errors.has_sanctioned_owner" class="text-sm text-red-600 mt-1">{{ form.errors.has_sanctioned_owner }}</p>
                    </div>

                    <div>
                        <p class="text-sm text-gray-800 font-medium mb-2">Ineligible business certification<span class="text-red-500"> *</span></p>
                        <div class="max-h-48 overflow-y-auto border rounded-md p-3 text-xs text-gray-600 space-y-1">
                            <p v-for="(item, i) in config.ineligible_businesses" :key="i">{{ i + 1 }}. {{ item }}</p>
                        </div>
                        <label class="flex items-start gap-2 mt-3 text-sm text-gray-800">
                            <input type="checkbox" v-model="form.certified_not_ineligible" class="mt-1 h-4 w-4 rounded text-[#2A375E] focus:ring-[#2A375E]" />
                            <span>I certify that my business is NOT any of the categories listed above.</span>
                        </label>
                        <p v-if="form.errors.certified_not_ineligible" class="text-sm text-red-600">{{ form.errors.certified_not_ineligible }}</p>
                    </div>

                    <!-- RAC-10281: explicit permissible-purpose certification -->
                    <div>
                        <p class="text-sm text-gray-800 font-medium mb-2">Permissible purpose certification<span class="text-red-500"> *</span></p>
                        <div class="border rounded-md p-3 text-xs text-gray-600">
                            {{ config.permissible_purpose_certification }}
                        </div>
                        <label class="flex items-start gap-2 mt-3 text-sm text-gray-800">
                            <input type="checkbox" v-model="form.permissible_purpose_certified" class="mt-1 h-4 w-4 rounded text-[#2A375E] focus:ring-[#2A375E]" />
                            <span>I certify the permissible purpose above.</span>
                        </label>
                        <p v-if="form.errors.permissible_purpose_certified" class="text-sm text-red-600">{{ form.errors.permissible_purpose_certified }}</p>
                    </div>
                </section>

                <!-- Step 4: Documents -->
                <section v-if="currentStep === 4" class="space-y-6">
                    <div>
                        <h3 class="text-lg font-bold text-gray-900">Upload the following documents based on your business type:</h3>
                        <p class="text-sm italic text-gray-500">(PDF, JPG, PNG, or Word, up to 20MB each.)</p>
                    </div>
                    <div v-for="(slot, idx) in documentSlots" :key="slot.key" class="space-y-1">
                        <label class="block text-sm font-bold text-gray-900">{{ idx + 1 }}. {{ slot.label }}<span class="text-red-500"> *</span></label>
                        <p class="text-sm italic text-gray-500 mb-1">Example: {{ slot.help }}</p>
                        <input
                            type="file"
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            @change="onFiles(slot.key, $event)"
                            class="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[#2A375E] file:text-white hover:file:opacity-90"
                        />
                        <p v-if="(form.documents[slot.key] || []).length" class="text-xs text-gray-600 mt-1">
                            {{ form.documents[slot.key].length }} file(s) selected
                        </p>
                        <p v-if="form.errors['documents.' + slot.key]" class="text-sm text-red-600">{{ form.errors['documents.' + slot.key] }}</p>
                    </div>
                </section>

                <!-- Step 5: Security controls questionnaire (RAC-10282) -->
                <section v-if="currentStep === SECURITY_STEP" class="space-y-6">
                    <div>
                        <h3 class="text-lg font-bold text-gray-900">Security controls</h3>
                        <p class="text-sm text-gray-500">Tell us how your organization protects consumer data. All questions are required.</p>
                    </div>
                    <div v-for="group in securityQuestions" :key="group.section" class="space-y-4">
                        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">{{ group.section }}</p>
                        <div v-for="q in group.questions" :key="q.key" class="space-y-2">
                            <p class="text-sm text-gray-800">{{ q.label }}<span class="text-red-500"> *</span></p>
                            <div class="flex gap-4">
                                <label class="flex items-center gap-2 text-sm"><input type="radio" :value="true" v-model="form.security_answers[q.key]" class="h-4 w-4 text-[#2A375E] focus:ring-[#2A375E]" /> Yes</label>
                                <label class="flex items-center gap-2 text-sm"><input type="radio" :value="false" v-model="form.security_answers[q.key]" class="h-4 w-4 text-[#2A375E] focus:ring-[#2A375E]" /> No</label>
                            </div>
                            <div v-if="q.detail_key && form.security_answers[q.key] === true">
                                <label class="block text-xs text-gray-600 mb-1">{{ q.detail_label }}</label>
                                <input type="text" v-model="form.security_answers[q.detail_key]" class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border" />
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Step 6: Site inspection (Appendix 1) -->
                <section v-if="currentStep === SITE_STEP" class="space-y-6">
                    <div>
                        <h3 class="text-lg font-bold text-gray-900">Site inspection (Appendix 1)</h3>
                        <p class="text-sm italic text-gray-500">
                            Experian requires a virtual site inspection. Each photo must have geocode/location metadata embedded — turn on
                            Location Services before taking photos, or the upload will be rejected. (JPEG, PNG, or WebP, up to 15MB each.)
                        </p>
                    </div>

                    <div class="space-y-4">
                        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">Required photos</p>
                        <div v-for="(label, kind) in config.site_photo_kinds" :key="kind" class="space-y-1">
                            <label class="block text-sm font-bold text-gray-900">{{ label }}</label>
                            <input
                                type="file"
                                multiple
                                accept=".jpg,.jpeg"
                                @change="onSitePhotos(kind, $event)"
                                class="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[#2A375E] file:text-white hover:file:opacity-90"
                            />
                            <p v-if="(form.site_photos[kind] || []).length" class="text-xs text-gray-600 mt-1">
                                {{ form.site_photos[kind].length }} photo(s) selected
                            </p>
                            <p v-if="form.errors['site_photos.' + kind] || form.errors['site_photos.' + kind + '.0']" class="text-sm text-red-600">
                                {{ form.errors['site_photos.' + kind] || form.errors['site_photos.' + kind + '.0'] }}
                            </p>
                        </div>
                    </div>

                    <div v-for="group in siteInspectionGroups" :key="group.section" class="space-y-4">
                        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">{{ group.section }}</p>
                        <div v-for="field in group.fields" :key="field.key">
                            <label class="block text-sm font-medium text-gray-700 mb-1">{{ field.label }}</label>
                            <div v-if="field.type === 'boolean'" class="flex gap-4 mt-1">
                                <label class="flex items-center gap-2 text-sm"><input type="radio" :value="false" v-model="form.site_inspection[field.key]" class="h-4 w-4 text-[#2A375E] focus:ring-[#2A375E]" /> No</label>
                                <label class="flex items-center gap-2 text-sm"><input type="radio" :value="true" v-model="form.site_inspection[field.key]" class="h-4 w-4 text-[#2A375E] focus:ring-[#2A375E]" /> Yes</label>
                            </div>
                            <select v-else-if="field.type === 'select'" v-model="form.site_inspection[field.key]" class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border bg-white">
                                <option value="">Select…</option>
                                <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
                            </select>
                            <textarea v-else-if="field.type === 'textarea'" v-model="form.site_inspection[field.key]" rows="3" class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border"></textarea>
                            <input v-else type="text" v-model="form.site_inspection[field.key]" class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border" />
                            <p v-if="form.errors['site_inspection.' + field.key]" class="text-sm text-red-600">{{ form.errors['site_inspection.' + field.key] }}</p>
                        </div>
                    </div>

                    <!-- Appendix 1 Acknowledgment (verbatim FCRA text) -->
                    <div class="space-y-2 pt-2 border-t">
                        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">Acknowledgment</p>
                        <div class="border rounded-md p-3 text-xs text-gray-600">{{ config.site_inspection_acknowledgment.body }}</div>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input type="text" v-model="form.site_inspection.acknowledgment.name" placeholder="Type your name *" class="rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border" />
                            <input type="text" v-model="form.site_inspection.acknowledgment.title" placeholder="Title (optional)" class="rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border" />
                        </div>
                        <label class="flex items-start gap-2 text-sm text-gray-800">
                            <input type="checkbox" v-model="form.site_inspection.acknowledgment.accepted" class="mt-1 h-4 w-4 rounded text-[#2A375E] focus:ring-[#2A375E]" />
                            <span>I confirm and accept the acknowledgment above.</span>
                        </label>
                        <p v-if="form.errors['site_inspection.acknowledgment.name']" class="text-sm text-red-600">{{ form.errors['site_inspection.acknowledgment.name'] }}</p>
                        <p v-if="form.errors['site_inspection.acknowledgment.accepted']" class="text-sm text-red-600">{{ form.errors['site_inspection.acknowledgment.accepted'] }}</p>
                    </div>
                </section>

                <!-- Steps 6..N: one acknowledgment per sub-step -->
                <section v-if="isSignatureStep && currentAck" class="space-y-6">
                    <p class="text-sm text-gray-600">
                        Acknowledgment {{ currentStep - FIRST_SIG_STEP + 1 }} of {{ SIG_COUNT }} — please review and sign.
                    </p>
                    <div class="border rounded-md p-4">
                        <h3 class="text-sm font-semibold text-gray-800">{{ currentAck.title }}</h3>
                        <p class="text-xs text-gray-600 mt-1 mb-3">{{ currentAck.body }}</p>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                            <input type="text" v-model="form.signatures[currentAck.kind].signer_name" placeholder="Signer full name *" class="rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border" />
                            <input type="text" v-model="form.signatures[currentAck.kind].signer_title" placeholder="Title (optional)" class="rounded-md border-gray-300 shadow-sm sm:text-sm px-3 py-2 border" />
                        </div>
                        <SignaturePad :key="currentAck?.kind" :ref="el => setPad(currentAck?.kind, el)" label="Signature" :required="true" height="160px" helper-text="Draw your signature above" />
                        <p v-if="form.errors['signatures.' + currentAck.kind + '.signature_base64'] || sigErrors[currentAck.kind]" class="text-sm text-red-600 mt-1">
                            {{ form.errors['signatures.' + currentAck.kind + '.signature_base64'] || sigErrors[currentAck.kind] }}
                        </p>
                        <p v-if="form.errors['signatures.' + currentAck.kind + '.signer_name']" class="text-sm text-red-600">{{ form.errors['signatures.' + currentAck.kind + '.signer_name'] }}</p>
                    </div>
                </section>

                <!-- Final step: Review -->
                <section v-if="isReviewStep" class="space-y-3 text-sm">
                    <h2 class="text-base font-semibold text-gray-800">Review &amp; submit</h2>
                    <ReviewRow label="Business type" :value="config.business_types[form.business_type]" />
                    <ReviewRow label="Legal entity" :value="form.legal_entity_name" />
                    <ReviewRow label="Physical address" :value="form.physical_address" />
                    <ReviewRow label="Corporate email" :value="form.company_email" />
                    <ReviewRow label="Principals" :value="form.principals.map(p => p.name).filter(Boolean).join(', ')" />
                    <ReviewRow label="Documents" :value="documentSummary" />
                    <ReviewRow label="Acknowledgments" :value="signedCount + ' of ' + SIG_COUNT + ' signed'" />
                    <p class="text-xs text-gray-500 pt-2">
                        After submitting your application, the RentRisk team will review and be in touch with next steps.
                    </p>
                    <p v-if="form.errors.registration" class="text-sm text-red-600">{{ form.errors.registration }}</p>
                </section>

                <p v-if="stepError" class="mt-6 text-sm text-red-600">{{ stepError }}</p>

                <!-- Nav -->
                <div class="flex items-center justify-between mt-8 pt-4 border-t">
                    <button
                        v-if="currentStep > 0"
                        type="button"
                        @click="back"
                        class="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Back
                    </button>
                    <span v-else></span>

                    <button
                        v-if="currentStep < steps.length - 1"
                        type="button"
                        @click="next"
                        class="px-5 py-2 text-sm font-semibold text-white theme_color rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Continue
                    </button>
                    <button
                        v-else
                        type="button"
                        @click="submit"
                        :disabled="form.processing || form.has_sanctioned_owner"
                        class="px-5 py-2 text-sm font-semibold text-white theme_color rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {{ form.processing ? 'Submitting…' : 'Submit application' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { Head, Link, useForm } from '@inertiajs/vue3'
import { computed, nextTick, reactive, ref } from 'vue'
import axios from 'axios'
import JetAuthenticationCardLogoColored from '@/Jetstream/AuthenticationCardLogoColored.vue'
import SignaturePad from '@/Components/SignaturePad.vue'
import FieldText from '@/Pages/Enterprise/Partials/FieldText.vue'
import ReviewRow from '@/Pages/Enterprise/Partials/ReviewRow.vue'

const props = defineProps({
    application: { type: Object, default: () => ({}) },
    config: { type: Object, required: true },
})

// Each acknowledgment is its own sub-step (RAC-9763 review: one combined page
// with five signatures felt daunting). The signature steps are built from the
// config so the count stays in sync with the acknowledgment list.
// 'Security controls' (RAC-10282) and 'Site inspection' (RAC-10283) are both
// appended to the base steps, after Documents and before the acknowledgment
// sub-steps. Existing step indices are unchanged and FIRST_SIG_STEP shifts
// automatically off baseSteps.length. Each new step resolves its own index via
// indexOf so the two constants never collide (both used to be baseSteps.length-1).
const baseSteps = ['Get started', 'Business details', 'Principals', 'Attestations', 'Documents', 'Security controls', 'Site inspection']
const SECURITY_STEP = baseSteps.indexOf('Security controls')
const SITE_STEP = baseSteps.indexOf('Site inspection')
const acks = props.config.acknowledgments
const steps = [...baseSteps, ...acks.map(a => a.title), 'Review']

const FIRST_SIG_STEP = baseSteps.length
const SIG_COUNT = acks.length

const app = props.application || {}

// Resume where the user left off — but never past Documents. Browsers can't
// restore a file picker or canvas, so uploaded files and signatures are
// re-entered; the typed data from steps 1–4 is preserved.
const RESUME_CAP = 4
const resumedStep = Math.min(Number(app.current_step || 0), RESUME_CAP)
const currentStep = ref(resumedStep)
const resumed = ref(resumedStep > 0)
const progress = computed(() => Math.round(((currentStep.value + 1) / steps.length) * 100))

// Prefill from any signatures saved on the draft so they resume across devices.
const savedSignatures = app.signatures || {}
const signatureState = {}
for (const ack of props.config.acknowledgments) {
    const saved = savedSignatures[ack.kind] || {}
    signatureState[ack.kind] = {
        signer_name: saved.signer_name || '',
        signer_title: saved.signer_title || '',
        signature_base64: saved.signature_base64 || '',
    }
}

const documentState = {}
for (const type of Object.keys(props.config.document_requirements)) {
    for (const slot of props.config.document_requirements[type]) {
        documentState[slot.key] = documentState[slot.key] || []
    }
}

// RAC-10282 — Security Controls Questionnaire. Flat map of question key -> bool
// (null until answered) plus any free-text detail key, prefilled from a draft.
const securityQuestions = props.config.security_questions || []
const savedSecurity = app.security_answers || {}
const securityState = {}
for (const group of securityQuestions) {
    for (const q of group.questions) {
        securityState[q.key] = q.key in savedSecurity ? savedSecurity[q.key] : null
        if (q.detail_key) {
            securityState[q.detail_key] = savedSecurity[q.detail_key] || ''
        }
    }
}

// Appendix 1 site inspection (RAC-10283): one photo bucket per kind + the
// physical-security questionnaire, both driven by config.
const sitePhotoState = {}
for (const kind of Object.keys(props.config.site_photo_kinds || {})) {
    sitePhotoState[kind] = []
}
const savedSiteInspection = app.site_inspection || {}
const siteInspectionState = {}
for (const field of props.config.site_inspection_fields || []) {
    const saved = savedSiteInspection[field.key]
    // Booleans seed to null (no radio pre-selected) exactly like the security
    // questionnaire above: a "No" the applicant never gave must not be recorded
    // under the FCRA acknowledgment. Unanswered stays null through the nullable
    // backend rules and renders as "—" in the PDF and admin views.
    siteInspectionState[field.key] = saved ?? (field.type === 'boolean' ? null : '')
}
// Appendix 1 FCRA acknowledgment, captured with the site inspection.
const savedAck = savedSiteInspection.acknowledgment || {}
siteInspectionState.acknowledgment = {
    name: savedAck.name || '',
    title: savedAck.title || '',
    accepted: savedAck.accepted || false,
}

const form = useForm({
    business_type: app.business_type || '',
    legal_entity_name: app.legal_entity_name || '',
    dbas: Array.isArray(app.dbas) && app.dbas.length ? app.dbas : [''],
    physical_address: app.physical_address || '',
    phone: app.phone || '',
    company_website: app.company_website || '',
    company_email: app.company_email || '',
    num_property_managers: app.num_property_managers || '',
    time_in_business: app.time_in_business || '',
    ownership_type: app.ownership_type || '',
    tax_id: app.tax_id || '',
    business_type_description: app.business_type_description || '',
    sos_registration_number: app.sos_registration_number || '',
    sos_registration_state: app.sos_registration_state || '',
    pm_license_number: app.pm_license_number || '',
    pm_license_state: app.pm_license_state || '',
    responsible_officer: {
        name: app.responsible_officer?.name || '',
        title: app.responsible_officer?.title || '',
        ownership_percentage: app.responsible_officer?.ownership_percentage ?? '',
        email: app.responsible_officer?.email || '',
        phone: app.responsible_officer?.phone || '',
    },
    principals: Array.isArray(app.principals) && app.principals.length ? app.principals : [{ name: '', title: '' }],
    agents: Array.isArray(app.agents) ? app.agents : [],
    has_sanctioned_owner: app.has_sanctioned_owner ?? false,
    certified_not_ineligible: app.certified_not_ineligible ?? false,
    permissible_purpose_certified: app.permissible_purpose_certified ?? false,
    documents: documentState,
    security_answers: securityState, // RAC-10282
    site_photos: sitePhotoState,
    site_inspection: siteInspectionState,
    signatures: signatureState,
})

// Appendix 1 checklist fields grouped by their config `section` for display.
const siteInspectionGroups = computed(() => {
    const groups = []
    for (const field of props.config.site_inspection_fields || []) {
        let group = groups.find(g => g.section === field.section)
        if (!group) { group = { section: field.section, fields: [] }; groups.push(group) }
        group.fields.push(field)
    }
    return groups
})

const documentSlots = computed(() => props.config.document_requirements[form.business_type] || [])

// §1.9.5 — mirror of the server-side CorporateEmailDomain rule so a free
// provider (Gmail etc.) is flagged the moment it's typed on step 0, instead
// of at final submit. The server rule remains the authoritative gate.
const rejectedEmailDomains = props.config.rejected_email_domains || []
const emailDomainError = computed(() => {
    const email = String(form.company_email || '').toLowerCase()
    const at = email.lastIndexOf('@')
    if (at === -1) { return '' }
    return rejectedEmailDomains.includes(email.slice(at + 1))
        ? 'Please use a corporate email address. Free providers (Gmail, Yahoo, Outlook, etc.) are not accepted.'
        : ''
})

const documentSummary = computed(() =>
    documentSlots.value
        .map(slot => `${slot.label}: ${(form.documents[slot.key] || []).length} file(s)`)
        .join('; ')
)

const onFiles = (key, event) => {
    form.documents[key] = Array.from(event.target.files || [])
}

const onSitePhotos = (kind, event) => {
    form.site_photos[kind] = Array.from(event.target.files || [])
}

// Which sub-step we're on. Each acknowledgment occupies one step between the
// Documents step and the final Review step.
const isSignatureStep = computed(() => currentStep.value >= FIRST_SIG_STEP && currentStep.value < FIRST_SIG_STEP + SIG_COUNT)
const currentAck = computed(() => isSignatureStep.value ? acks[currentStep.value - FIRST_SIG_STEP] : null)
const isReviewStep = computed(() => currentStep.value === steps.length - 1)
const signedCount = computed(() => acks.filter(a => form.signatures[a.kind].signature_base64).length)

// SignaturePad refs, keyed by acknowledgment kind. Only the current sub-step's
// pad is mounted at a time, so each signature is captured into form.signatures
// when the user advances (captureSignature). On return to a signed step we
// repaint the saved drawing so it isn't lost visually.
const pads = {}
const setPad = (kind, el) => {
    if (el) {
        pads[kind] = el
        const saved = form.signatures[kind]?.signature_base64
        if (saved) { nextTick(() => pads[kind]?.fromDataURL(saved)) }
    } else {
        delete pads[kind]
    }
}
const sigErrors = reactive({})
const stepError = ref('')

const filled = (v) => v !== '' && v !== null && v !== undefined

// Capture one acknowledgment's drawn signature into form.signatures and
// validate it. A previously-captured signature stays valid even if the freshly
// mounted pad reads empty (e.g. navigating Back then Forward).
const captureSignature = (kind) => {
    const pad = pads[kind]
    const sig = form.signatures[kind]
    if (pad && !pad.isEmpty()) {
        sig.signature_base64 = pad.save()
    }
    if (!sig.signature_base64) { sigErrors[kind] = 'Please sign this acknowledgment.'; return false }
    if (!String(sig.signer_name || '').trim()) { sigErrors[kind] = 'Enter the signer name.'; return false }
    sigErrors[kind] = ''
    return true
}

// Final gate before submit: every acknowledgment must have a signature + name.
// Returns the step index of the first incomplete acknowledgment, or -1 if all
// are complete.
const firstIncompleteSignatureStep = () => {
    for (let i = 0; i < acks.length; i++) {
        const sig = form.signatures[acks[i].kind]
        if (!sig.signature_base64 || !String(sig.signer_name || '').trim()) {
            sigErrors[acks[i].kind] = !sig.signature_base64 ? 'Please sign this acknowledgment.' : 'Enter the signer name.'
            return FIRST_SIG_STEP + i
        }
    }
    return -1
}

const validateStep = (step) => {
    stepError.value = ''
    if (step === 0) {
        if (!filled(form.company_email)) { stepError.value = 'Please enter your corporate email.'; return false }
        if (emailDomainError.value) { stepError.value = emailDomainError.value; return false }
        if (!form.business_type) { stepError.value = 'Please choose a business type.'; return false }
    }
    if (step === 1) {
        const need = ['legal_entity_name', 'physical_address', 'phone', 'time_in_business', 'ownership_type', 'tax_id', 'sos_registration_number', 'sos_registration_state']
        if (!need.every(f => filled(form[f]))) { stepError.value = 'Please complete all required fields.'; return false }
        if (!filled(form.responsible_officer.name) || !filled(form.responsible_officer.title) || !filled(form.responsible_officer.email)) {
            stepError.value = 'Please provide the responsible owner/officer name, title, and email.'
            return false
        }
    }
    if (step === 2 && !form.principals.some(p => String(p.name || '').trim())) {
        stepError.value = 'List at least one principal or owner.'
        return false
    }
    if (step === 3) {
        if (form.has_sanctioned_owner) { stepError.value = 'We can’t continue online based on your sanctioned-country answer. Please contact RentRisk directly.'; return false }
        if (!form.certified_not_ineligible) { stepError.value = 'You must certify your business is not on the ineligible list.'; return false }
        if (!form.permissible_purpose_certified) { stepError.value = 'You must certify the permissible purpose for obtaining consumer reports.'; return false }
    }
    if (step === 4) {
        for (const slot of documentSlots.value) {
            if (!(form.documents[slot.key] || []).length) { stepError.value = `Please upload: ${slot.label}.`; return false }
        }
    }
    // RAC-10283 — the Appendix 1 FCRA acknowledgment must be signed + accepted.
    if (step === SITE_STEP) {
        const ack = form.site_inspection.acknowledgment
        if (!ack.accepted || !String(ack.name || '').trim()) {
            stepError.value = 'Type your name and accept the site inspection acknowledgment to continue.'
            return false
        }
    }
    // RAC-10282 — every security question must be answered yes/no.
    if (step === SECURITY_STEP) {
        for (const group of securityQuestions) {
            for (const q of group.questions) {
                if (form.security_answers[q.key] !== true && form.security_answers[q.key] !== false) {
                    stepError.value = 'Please answer every security question.'
                    return false
                }
            }
        }
    }
    if (step >= FIRST_SIG_STEP && step < FIRST_SIG_STEP + SIG_COUNT) {
        if (!captureSignature(acks[step - FIRST_SIG_STEP].kind)) {
            stepError.value = 'Please sign this acknowledgment and enter the signer name.'
            return false
        }
    }
    return true
}

// Best-effort background autosave of typed data + position so a refresh or a
// return-later resumes the form. Files and signatures are intentionally excluded.
const persistDraft = () => {
    axios.post(route('enterprise.intake.save-draft'), {
        business_type: form.business_type,
        legal_entity_name: form.legal_entity_name,
        dbas: form.dbas,
        physical_address: form.physical_address,
        phone: form.phone,
        company_website: form.company_website,
        company_email: form.company_email,
        num_property_managers: form.num_property_managers || null,
        time_in_business: form.time_in_business,
        ownership_type: form.ownership_type,
        tax_id: form.tax_id,
        business_type_description: form.business_type_description,
        sos_registration_number: form.sos_registration_number,
        sos_registration_state: form.sos_registration_state,
        pm_license_number: form.pm_license_number,
        pm_license_state: form.pm_license_state,
        responsible_officer: form.responsible_officer,
        principals: form.principals,
        agents: form.agents,
        has_sanctioned_owner: form.has_sanctioned_owner,
        certified_not_ineligible: form.certified_not_ineligible,
        permissible_purpose_certified: form.permissible_purpose_certified,
        // Signatures persist across devices; file uploads can't (a browser
        // can't repopulate a file picker), so resume still lands on Documents.
        signatures: form.signatures,
        current_step: Math.min(currentStep.value, RESUME_CAP),
    }).catch(() => { /* autosave is best-effort; the form still works without it */ })
}

const back = () => {
    stepError.value = ''
    if (currentStep.value > 0) { currentStep.value--; persistDraft() }
}

const next = () => {
    if (!validateStep(currentStep.value)) { return }
    if (currentStep.value < steps.length - 1) { currentStep.value++ }
    persistDraft()
}

const submit = () => {
    const incomplete = firstIncompleteSignatureStep()
    if (incomplete !== -1) {
        stepError.value = 'Please sign each acknowledgment and enter the signer name.'
        currentStep.value = incomplete
        return
    }
    form.transform(data => ({ ...data, num_property_managers: data.num_property_managers || null }))
        .post(route('enterprise.intake.submit'), {
            forceFormData: true,
            // A rejected email domain (e.g. a resumed draft seeded with a free
            // provider at signup) renders on step 0 — jump there so the error
            // is never invisible behind the Review step.
            onError: (errors) => { if (errors.company_email) { currentStep.value = 0 } },
        })
}
</script>
